/**
 * Widget Library Detail Page
 * View template details and preview
 */

import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Star, Users, Monitor, Smartphone, Crown, Sparkles, Settings } from 'lucide-react';
import { Button, Typography } from '@/design-system/components';
import { colors } from '@/design-system/theme/colors';
import { useTemplate } from '../api';

export function DetailPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { template, isLoading } = useTemplate(templateId);

  const handleBack = useCallback(() => {
    navigate('/templates');
  }, [navigate]);

  const handleCustomize = useCallback(() => {
    if (template) {
      navigate(`/customize/${template.id}`);
    }
  }, [navigate, template]);

  if (isLoading) {
    return (
      <div className="h-full" style={{ backgroundColor: colors.surfaceLightest }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 rounded w-48" style={{ backgroundColor: colors.surfaceLighter }} />
            <div className="h-64 rounded-xl" style={{ backgroundColor: colors.surfaceLighter }} />
            <div className="h-32 rounded-xl" style={{ backgroundColor: colors.surfaceLighter }} />
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="h-full" style={{ backgroundColor: colors.surfaceLightest }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16 rounded-xl" style={{ backgroundColor: colors.white }}>
            <Typography variant="h4" color={colors.textInverse} className="mb-2">
              Template Not Found
            </Typography>
            <Typography variant="body2" color={colors.textMuted} className="mb-6">
              The template you are looking for does not exist.
            </Typography>
            <Button variant="primary" onClick={handleBack}>
              Back to Library
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full" style={{ backgroundColor: colors.surfaceLightest }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button variant="ghost" size="medium" leftIcon={<ArrowLeft size={18} />} onClick={handleBack} className="mb-6">
          Back to Library
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Preview */}
          <div>
            <div
              className="aspect-video rounded-xl flex items-center justify-center relative overflow-hidden"
              style={{
                background:
                  template.theme === 'dark-mode'
                    ? 'linear-gradient(135deg, #1F2937 0%, #111827 100%)'
                    : template.theme === 'gradient'
                      ? 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
                      : `linear-gradient(135deg, ${template.themeStyles.primaryColor}20 0%, ${template.themeStyles.secondaryColor}20 100%)`,
              }}
            >
              {/* Widget Preview Mock */}
              <div
                className="w-80 rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: template.themeStyles.backgroundColor,
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                }}
              >
                {/* Header */}
                <div className="p-4" style={{ backgroundColor: template.themeStyles.primaryColor }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20" />
                    <div>
                      <div className="text-white font-semibold text-sm">Your Brand</div>
                      <div className="text-white/80 text-xs">Typically replies instantly</div>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="p-4 space-y-2" style={{ backgroundColor: '#ECE5DD', minHeight: 100 }}>
                  <div
                    className="px-3 py-2 rounded-lg text-sm max-w-[80%]"
                    style={{ backgroundColor: colors.white, color: template.themeStyles.textColor }}
                  >
                    Hi! How can we help you today?
                  </div>
                </div>

                {/* Footer */}
                <div className="p-3 border-t" style={{ borderColor: colors.surfaceSubtle }}>
                  <Button
                    variant="primary"
                    size="medium"
                    fullWidth
                    style={{ backgroundColor: template.themeStyles.primaryColor }}
                  >
                    {template.featureConfig.buttonText || 'Start Chat'}
                  </Button>
                </div>
              </div>

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {template.isNew && (
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ backgroundColor: colors.successBgSubtle, color: colors.successDarker }}
                  >
                    <Sparkles size={12} />
                    New
                  </span>
                )}
                {template.isPremium && (
                  <span
                    className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                    style={{ backgroundColor: colors.warningBgSubtle, color: colors.warningDarker }}
                  >
                    <Crown size={12} />
                    Premium
                  </span>
                )}
              </div>
            </div>

            {/* Device Toggle */}
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                className="p-3 rounded-lg transition-colors"
                style={{ backgroundColor: colors.primaryExtraLight, color: colors.primary }}
              >
                <Monitor size={20} />
              </button>
              <button
                className="p-3 rounded-lg transition-colors"
                style={{ backgroundColor: colors.surfaceLighter, color: colors.textMuted }}
              >
                <Smartphone size={20} />
              </button>
            </div>
          </div>

          {/* Right: Details */}
          <div>
            <div className="rounded-xl p-6" style={{ backgroundColor: colors.white }}>
              {/* Header */}
              <div className="mb-6">
                <Typography variant="h3" color={colors.textInverse} className="mb-2">
                  {template.name}
                </Typography>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <Star size={16} color={colors.warning} fill={colors.warning} />
                    <span style={{ fontWeight: 600, color: colors.textInverse }}>{template.rating}</span>
                    <span style={{ color: colors.textMuted }}>/5</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users size={16} color={colors.textMuted} />
                    <span style={{ color: colors.textMuted }}>{template.usageCount.toLocaleString()} uses</span>
                  </div>
                </div>

                <Typography variant="body1" color={colors.textMuted}>
                  {template.description}
                </Typography>
              </div>

              {/* Specifications */}
              <div className="border-t pt-6 mb-6" style={{ borderColor: colors.surfaceSubtle }}>
                <Typography variant="subtitle2" color={colors.textInverse} className="mb-4">
                  Specifications
                </Typography>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Typography variant="caption" color={colors.textMuted}>
                      Theme
                    </Typography>
                    <Typography variant="body2" color={colors.textInverse} className="capitalize">
                      {template.theme.replace('-', ' ')}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color={colors.textMuted}>
                      Placement
                    </Typography>
                    <Typography variant="body2" color={colors.textInverse} className="capitalize">
                      {template.placement.replace(/-/g, ' ')}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color={colors.textMuted}>
                      Feature
                    </Typography>
                    <Typography variant="body2" color={colors.textInverse} className="capitalize">
                      {template.featureType.replace(/-/g, ' ')}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color={colors.textMuted}>
                      Category
                    </Typography>
                    <Typography variant="body2" color={colors.textInverse} className="capitalize">
                      {template.category.replace('-', ' ')}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Colors */}
              <div className="border-t pt-6 mb-6" style={{ borderColor: colors.surfaceSubtle }}>
                <Typography variant="subtitle2" color={colors.textInverse} className="mb-4">
                  Default Colors
                </Typography>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg"
                      style={{ backgroundColor: template.themeStyles.primaryColor }}
                    />
                    <span style={{ fontSize: 13, color: colors.textMuted }}>Primary</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg"
                      style={{ backgroundColor: template.themeStyles.secondaryColor }}
                    />
                    <span style={{ fontSize: 13, color: colors.textMuted }}>Secondary</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg border"
                      style={{
                        backgroundColor: template.themeStyles.backgroundColor,
                        borderColor: colors.surfaceSubtle,
                      }}
                    />
                    <span style={{ fontSize: 13, color: colors.textMuted }}>Background</span>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="border-t pt-6 mb-6" style={{ borderColor: colors.surfaceSubtle }}>
                <Typography variant="subtitle2" color={colors.textInverse} className="mb-4">
                  Tags
                </Typography>

                <div className="flex flex-wrap gap-2">
                  {template.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs"
                      style={{ backgroundColor: colors.surfaceSubtle, color: colors.textMuted }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="primary" size="large" fullWidth leftIcon={<Settings size={18} />} onClick={handleCustomize}>
                  Customize This Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetailPage;
